import React, { useState, useEffect } from "react";
import { db, storage, cloudfunction } from "../firebase";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { 
  Card, 
  Button, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Grid,
  Box,
  Typography,
  Input
} from "@mui/material";
import { muiBbtn } from "./style";

export default function SendNotifications() {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const userList = querySnapshot.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setUsers(userList);
      } catch (error) {
        console.error("Error fetching users:", error);
        alert("Failed to fetch users");
      }
    };
    fetchUsers();
  }, []);

  const handleSelectUser = (uid) => {
    setSelectedUsers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.uid));
    }
    setSelectAll(!selectAll);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      const imageRef = ref(storage, `notifications/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const sendPushNotification = async (deviceTokens, notificationData) => {
    try {
      // Call Firebase Cloud Function to send push notifications
      const sendNotificationFunction = httpsCallable(cloudfunction, "sendPushNotification");
      
      const result = await sendNotificationFunction({
        deviceTokens: deviceTokens,
        notification: {
          title: notificationData.title,
          body: notificationData.subtitle,
          image: notificationData.imageUrl
        },
        data: {
          type: 'admin_announcement',
          title: notificationData.title,
          subtitle: notificationData.subtitle,
          image: notificationData.imageUrl
        }
      });
      
      return result.data.success;
    } catch (error) {
      console.error("Error sending push notification:", error);
      // If cloud function doesn't exist yet, we'll still save to database
      console.warn("Push notification cloud function not available. Notification saved to database only.");
      return false;
    }
  };

  const saveNotificationToDatabase = async (notificationData) => {
    try {
      const notificationDoc = {
        title: notificationData.title,
        message: notificationData.subtitle,
        image: notificationData.imageUrl || "",
        type: "admin_announcement",
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        uid: "admin", // or current admin user ID
        recipients: selectedUsers
      };

      await addDoc(collection(db, "notifications"), notificationDoc);
      return true;
    } catch (error) {
      console.error("Error saving notification:", error);
      return false;
    }
  };

  const sendNotifications = async () => {
    if (!title || !subtitle || selectedUsers.length === 0) {
      alert("Please fill in title, subtitle and select at least one user.");
      return;
    }

    setLoading(true);

    try {
      // Upload image if provided
      let uploadedImageUrl = "";
      if (imageFile) {
        uploadedImageUrl = await handleImageUpload(imageFile);
        setImageUrl(uploadedImageUrl);
      }

      const notificationData = {
        title,
        subtitle,
        imageUrl: uploadedImageUrl
      };

      // Get device tokens for selected users
      const selectedUsersData = users.filter(user => selectedUsers.includes(user.uid));
      const deviceTokens = selectedUsersData
        .map(user => user.deviceToken)
        .filter(token => token); // Filter out undefined/null tokens

        console.log(deviceTokens);
      // Send push notifications
      if (deviceTokens.length > 0) {
        await sendPushNotification(deviceTokens, notificationData);
      }

      // Save notification to database
      await saveNotificationToDatabase(notificationData);

      alert("Notifications sent successfully!");
      
      // Reset form
      setSelectedUsers([]);
      setSelectAll(false);
      setSearch("");
      setTitle("");
      setSubtitle("");
      setImageFile(null);
      setImageUrl("");

    } catch (error) {
      console.error("Error sending notifications:", error);
      alert("Failed to send notifications: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) =>
    (user.displayName?.toLowerCase().includes(search.toLowerCase()) ||
     user.email?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ width: "100%", padding: 40, backgroundColor: "#212C62" }}>
      <Card style={{ padding: 50, borderRadius: 10 }}>
        <h1 style={{ textAlign: "left", color: "#212C62" }}>
          <b>Send Custom Notifications</b>
        </h1>
        
        <TextField
          label="Notification Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
        />
        
        <TextField
          label="Notification Subtitle/Message"
          fullWidth
          multiline
          rows={3}
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          margin="normal"
          required
        />

        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" style={{ color: "#212C62", marginBottom: 10 }}>
            Upload Image (Optional)
          </Typography>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            style={{ marginBottom: 10 }}
          />
          {imageFile && (
            <Typography variant="body2" color="textSecondary">
              Selected: {imageFile.name}
            </Typography>
          )}
        </Box>

        <Button 
          variant="contained" 
          color="primary" 
          style={{ ...muiBbtn, marginTop: 20 }} 
          onClick={sendNotifications}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send Notification"}
        </Button>

        <h5 style={{ marginTop: 30, color: "#212C62" }}>Select Users:</h5>
        
        <TextField
          label="Search Users"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="normal"
        />
        
        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label="Select All"
        />
        
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.uid}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedUsers.includes(user.uid)}
                    onChange={() => handleSelectUser(user.uid)}
                  />
                }
                label={`${user.displayName || 'No Name'} (${user.email || 'No Email'})`}
              />
            </Grid>
          ))}
        </Grid>

        {filteredUsers.length === 0 && search && (
          <Typography variant="body2" style={{ marginTop: 20, color: "#666" }}>
            No users found matching your search.
          </Typography>
        )}
      </Card>
    </div>
  );
}
